import {
  Canvas,
  CameraType,
  Color,
  Element,
  ImageCenteredElement,
  RectElement,
  RectCenteredElement,
  CursorType,
} from '@thive/canvas';
import {
  Areas,
  Templates,
  InterAreaConnection,
  Obstacle,
  LogicalSpace,
  Station,
  Track,
  VehicleLayout,
  JustInTimeRoutingConfig,
  FlowLayer,
  Group,
} from '@thive/fddm-types';
import { Vector2, Vector3 } from 'three';
import { v4 as uuid } from 'uuid';

import { assertNever } from '@/helpers/utils';
import { useLodStore } from '@/modules/lod/useLodStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FloorplanInteractionMode } from '@/modules/app/types';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  areasToFlatCollection,
  buildTagMatchRegExp,
  templatesToFlatCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import {
  FDDM_AREA_TAGS,
  FDDM_INTERACTIVE_TAGS,
  FDDM_TAG,
  INTERACTIVITY_OFF,
  INTERACTIVITY_MODE_MAP,
  Tag,
  TAG_PATTERN_2D_ELEMENTS,
  CONNECTION_BUBBLE_INTERACTIVITY_MODE_MAP,
  SEGMENT_ELEMENT_INTERACTIVITY_MODE_MAP,
} from '@/modules/floorplanDesign/constants';
import { modifyColor } from '@/modules/floorplanDesign/mappers/colors';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { Area } from '@/modules/floorplanDesign/types';
import { isIntersect } from '@/modules/common/helpers/array';
import { NumUtils } from '@/modules/common/utils';
import {
  createVehicleGeneratedLayerName,
  GeneratedLayerNames,
  LayerNames,
  LayerVisibilityMode,
} from '@/modules/common/types/layers';
import { getMiddlePoint, roundToDecimals } from '@/modules/common/helpers/math';
import { logger } from '@common/logging/logger';
import { InterAreaConnectionUtils } from '@/modules/connections/interAreaConnection';
import { AlignMode } from '@/modules/workspace/types/align';
import { ToolId } from '@modules/toolbar/types';
import { isDrawableToolId, isSegmentToolId } from '@/modules/toolbar/guards';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { UserPreferenceName, getUserPreference } from '@/modules/userPreferences';
import { useLayerStore } from '@/modules/layers/store/useLayerStore';
import { getEffectiveLayerVisibility } from '@/modules/layers/visibility';
import {
  FDDM_LAYER_DEFINITIONS,
  getLayerDefinitionsForTag,
  getLayerDefinition,
  LayerDefinition,
} from '@/modules/layers/layerTree';
import { calcScalingElementsSize as calcReferenceScalingElementsSize } from '@/modules/referenceImage/helpers';
import { REFERENCE_SCALE_ELEMENT_STROKE_WIDTH } from '@/modules/referenceImage/constants';
import { buildOriginElements, ORIGIN_GROUP_ID, OriginElementSizing } from '@/modules/origin';
import { ORIGIN_ELEMENT_IDS } from '@/modules/origin/constants';

import { useCanvasStore } from '@/modules/canvas/store';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import {
  calcBubbleElementScales,
  calcHighwayArrowScale,
  calcJitPointSize,
  calcRackScale,
  calcScaleFactorBasedOnCanvasNativeZoom,
} from '@/modules/canvas/helpers';
import {
  AREA_CONNECTION_POINT_COLOR,
  AREA_CONNECTION_POINT_RADIUS,
  Z_INDEX_MAP,
} from '@/modules/canvas/constants/elements';
import { elementFactory } from '@/modules/canvas/factories';
import { UpdateElementsOptions } from '@/modules/canvas/types';
import { isStableRectCenteredElement } from '@/modules/canvas/typeguards';
import { METERS_TO_MILLIMETERS_FACTOR } from '../../factories/element';
import { toolIdToCanvasTool, toolIdToColor } from '../../helpers/tool';
import { ZoomFactorUtils } from './utils';
import { FloorplanElementMapper } from '../../mappers/FloorplanElementMapper';

const Transparency = {
  DEFAULT: 68,
  DIM: 34,
} as const;

export class CanvasServiceImpl implements ICanvasService {
  private canvasStore: typeof useCanvasStore;
  private lodStore: typeof useLodStore;
  private appStore: typeof useAppStore;
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private vehicleStore: typeof useVehicleStore;
  private interactivityMode: FloorplanInteractionMode = 'READONLY';

  public constructor(
    canvasStore: typeof useCanvasStore,
    lodStore: typeof useLodStore,
    appStore: typeof useAppStore,
    floorplanDesignStore: typeof useFloorplanDesignStore,
    vehicleStore: typeof useVehicleStore,
  ) {
    this.canvasStore = canvasStore;
    this.lodStore = lodStore;
    this.appStore = appStore;
    this.floorplanDesignStore = floorplanDesignStore;
    this.vehicleStore = vehicleStore;
  }

  public setCameraType(cameraType: CameraType): void {
    this.getCanvas().setCameraType(cameraType);
  }

  public deselectAll(): void {
    this.getCanvas().deselectAll();
  }

  public lockSelection(): void {
    this.getCanvas().lockSelection();
  }

  public unlockSelection(): void {
    this.getCanvas().unlockSelection();
  }

  public setOriginInteractive(interactive: boolean): void {
    const canvas = this.getCanvas();
    const originChildIds = ORIGIN_ELEMENT_IDS.filter((id) => id !== ORIGIN_GROUP_ID);

    if (interactive) {
      canvas.unlockSelection();
    }

    canvas.updateInteractivity([ORIGIN_GROUP_ID, ...originChildIds], {
      ...INTERACTIVITY_OFF,
      clickable: interactive,
      hoverable: interactive,
      selectable: interactive,
      draggable: interactive,
    });
  }

  public removeCanvasElements(
    params: { method: 'byId'; ids: string[] } | { method: 'byTag'; tags: Tag[] },
  ): void {
    const canvas = this.getCanvas();
    const { method } = params;

    switch (method) {
      case 'byId':
        canvas.removeElements(params.ids);
        break;
      case 'byTag': {
        const pattern = buildTagMatchRegExp(params.tags);
        canvas.removeElements(canvas.getElementsByTag(pattern).map((element) => element.id));
        break;
      }
      default:
        assertNever(method);
    }
  }

  public addAreaConnectionPoints(areaIds: string[]): void {
    const { getAreasByIds } = this.floorplanDesignStore.getState();

    const connectionPointElements = areasToFlatCollection(getAreasByIds(areaIds)).flatMap((area) =>
      this.buildAreaConnectionPointElements(area),
    );

    if (connectionPointElements.length === 0) return;

    this.getCanvas().updateElements(connectionPointElements);
  }

  /* @description Updates the connection points for the given area elements based on their current dimensions, think this should be recalculated in the canvas instead preferably. */
  public updateAreaConnectionPoints(elements: readonly Element[]): void {
    const { getAreasByIds } = this.floorplanDesignStore.getState();

    const liveDimensionsById = new Map(
      elements
        .filter(isStableRectCenteredElement)
        .map(({ id, scale }): [string, { length: number; width: number }] => [
          id,
          {
            length: NumUtils.mmToMeters(Math.abs(scale.x)),
            width: NumUtils.mmToMeters(Math.abs(scale.y)),
          },
        ]),
    );

    if (liveDimensionsById.size === 0) return;

    // get areas by ids of the elements
    const connectionPointElements = areasToFlatCollection(
      getAreasByIds([...liveDimensionsById.keys()]),
    ).flatMap((area) => {
      const dimensions = liveDimensionsById.get(area.id);
      if (!dimensions || isHighwayArea(area)) return [];

      const { length, width } = area.rectangle;
      const isResized =
        NumUtils.metersToMmRounded(length) !== NumUtils.metersToMmRounded(dimensions.length) ||
        NumUtils.metersToMmRounded(width) !== NumUtils.metersToMmRounded(dimensions.width);

      return isResized ? this.buildAreaConnectionPointElements(area, dimensions) : [];
    });

    if (connectionPointElements.length === 0) return;

    this.getCanvas().updateElements(connectionPointElements);
  }

  private buildAreaConnectionPointElements(
    area: Area,
    dimensions?: { length: number; width: number },
  ): Element[] {
    const connectionPoints = InterAreaConnectionUtils.getConnectionPoints(area, dimensions);
    return connectionPoints.map(
      (position, index) =>
        elementFactory.buildCircleElement({
          id: `${area.id}-${FDDM_TAG.AREA_CONNECTION_POINT}-${index}`,
          parentId: area.id,
          position: { ...position, z: Z_INDEX_MAP[FDDM_TAG.AREA_CONNECTION_POINT] },
          radius: AREA_CONNECTION_POINT_RADIUS,
          color: AREA_CONNECTION_POINT_COLOR,
          tags: [FDDM_TAG.AREA_CONNECTION_POINT],
          interactivity: INTERACTIVITY_OFF,
        }),
    );
  }

  public removeAreaConnectionPoints(): void {
    this.removeCanvasElements({ method: 'byTag', tags: [FDDM_TAG.AREA_CONNECTION_POINT] });
  }

  public clearFloorplanElements() {
    const tagsToRetain: Tag[] = [FDDM_TAG.ORIGIN];

    this.removeCanvasElements({
      method: 'byTag',
      tags: Object.values(FDDM_TAG).filter((tag) => !tagsToRetain.includes(tag)),
    });
  }

  async updateElements(
    source: {
      areas?: Areas;
      obstacles?: Obstacle[];
      interAreaConnections?: InterAreaConnection[];
      logicalSpaces?: LogicalSpace[];
      jitRoutingConfigs?: JustInTimeRoutingConfig[];
      templates?: Templates;
      groups?: Group[];
      flowLayer?: FlowLayer;
      stations?: Station[];
      vehicleLayouts?: VehicleLayout[];
      tracks?: Track[];
      referenceImage?: ImageCenteredElement;
    },
    { preserveInteractivity = false }: UpdateElementsOptions = {},
  ): Promise<void> {
    const canvas = this.getCanvas();
    const zoom = canvas.getZoom();
    const { selectedIds } = this.appStore.getState();
    const layerVisibility = this.createLayerVisibilityResolver(zoom);
    const {
      design,
      floorplan,
      getAreasByIds,
      getGroupsByAreaIds,
      getRelatedFlows,
      getRelatedFlowStops,
      getJitRoutingConfigsByIds,
    } = this.floorplanDesignStore.getState();

    const mapper = new FloorplanElementMapper();

    const { elements, idsToRemove } = mapper.map2D({
      source,
      canvas,
      zoom,
      selectedIds,
      design,
      vehicleLayouts: floorplan.vehicleLayouts,
      vehicleDetails: this.vehicleStore.getState().vehicleDetails.getAll(),
      getAreasByIds,
      getGroupsByAreaIds,
      getRelatedFlows,
      getRelatedFlowStops,
      getJitRoutingConfigsByIds,
      isTagVisible: layerVisibility.isTagVisible,
      isLayerVisible: layerVisibility.isLayerVisible,
      isGeneratedLayerVisible: layerVisibility.isGeneratedLayerVisible,
    });

    const elementsToUpdate = preserveInteractivity
      ? elements.map((element) => this.withPreservedInteractivity(element, canvas))
      : elements;

    if (idsToRemove.length > 0) {
      this.removeCanvasElements({
        method: 'byId',
        ids: idsToRemove,
      });
    }

    await canvas.updateElementsAsync(elementsToUpdate);
    // Re-read selection after the async update so click-away is respected.
    const currentSelectedIds = this.appStore.getState().selectedIds;
    const existingSelectedIds = canvas
      .getElements(currentSelectedIds)
      .map((selectedElement) => selectedElement.id);

    if (existingSelectedIds.length > 0) {
      canvas.setSelection(existingSelectedIds);
    }
  }

  /**
   * @description
   * Removes the group from Canvas and clears the parentId if its areas
   */
  ungroup(groupId: string) {
    const canvas = this.getCanvas();

    const childIds = canvas.getChildrenIds(groupId);
    const elements = canvas.getElements(childIds);
    canvas.updateElements(
      elements.map((element) => ({
        ...element,
        interactivity: {
          ...element.interactivity,
          selectable: true,
          hoverable: true,
        },
      })),
    );
    canvas.ungroup(groupId);
  }

  /**
   * @description
   * Scale elements to the current zoom level
   */
  public scaleToCurrentZoom() {
    const canvas = this.getCanvas();
    const zoom = canvas.getZoom();

    // Scale the rack icons
    const rackElements = this.scaleIconElements(
      canvas.getElementsByTag(buildTagMatchRegExp([FDDM_TAG.RACK])),
      calcRackScale(zoom),
    );

    // Scale the highway arrows
    const arrowElements = this.scaleIconElements(
      canvas.getElementsByTag(buildTagMatchRegExp([FDDM_TAG.HIGHWAY_ARROW])),
      calcHighwayArrowScale(zoom),
    );

    // Scale the bubble connections
    const { radius, dividerStrokeWidth, arrowStrokeWidth } = calcBubbleElementScales(zoom);
    const connectionElements = (
      canvas.getElementsByTag(
        buildTagMatchRegExp([
          FDDM_TAG.BUBBLE_DIVIDER,
          FDDM_TAG.INTER_AREA_CONNECTION,
          FDDM_TAG.BUBBLE_ARROW,
          FDDM_TAG.CROSSING,
        ]),
      ) as Element[]
    ).map((element) => {
      if (element.type === 'Arc') {
        return { ...element, radius };
      }
      if (element.type === 'Line' && element.tags?.includes(FDDM_TAG.BUBBLE_DIVIDER)) {
        return {
          ...element,
          pointA: new Vector3(0, radius * 2, Z_INDEX_MAP[FDDM_TAG.BUBBLE_DIVIDER]),
          pointB: new Vector3(0, -radius * 2, Z_INDEX_MAP[FDDM_TAG.BUBBLE_DIVIDER]),
          strokeWidth: dividerStrokeWidth,
        };
      }
      if (element.type === 'Line' && element.tags?.includes(FDDM_TAG.DISTANT_CONNECTION)) {
        return {
          ...element,
          strokeWidth: dividerStrokeWidth,
        };
      }
      if (element.type === 'Arrow') {
        const index = element.tags?.includes(FDDM_TAG.A_TO_B_CONNECTION) ? -1 : 1;
        return {
          ...element,
          pointA: new Vector3(radius * index, radius * -index, Z_INDEX_MAP[FDDM_TAG.BUBBLE_ARROW]),
          pointB: new Vector3(radius * index, radius * index, Z_INDEX_MAP[FDDM_TAG.BUBBLE_ARROW]),
          strokeWidth: arrowStrokeWidth,
        };
      }
      if (element.type === 'Circle') {
        return { ...element, radius };
      }

      return {
        ...element,
      };
    });

    // Scale the jit routing points
    const size = calcJitPointSize(zoom);
    const jitRoutingPointElements = (
      canvas.getElementsByTag(buildTagMatchRegExp([FDDM_TAG.JIT_POINT])) as Element[]
    ).map((element) => {
      if (element.type === 'RectCentered') {
        return { ...element, size: new Vector2(size * 1000, size * 1000) };
      }

      return {
        ...element,
      };
    });

    // Scale the reference scaling elements (2 lines per user defined point)
    const sizes = calcReferenceScalingElementsSize(zoom);
    const referenceScalingPointElements = (
      canvas.getElementsByTag(
        buildTagMatchRegExp([
          FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A,
          FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B,
        ]),
      ) as Element[]
    ).map((element) => {
      if (!element.tags) return element;

      if (
        element.tags &&
        (element.tags.includes(FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A) ||
          element.tags.includes(FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B))
      ) {
        if (element.type !== 'Line') {
          logger.error(
            'Expected a reference scaling line end to be rendered with "Line" elements in the canvas. Got: ',
            element.type,
          );

          return element;
        }

        const currentPointA = element.pointA;
        const currentPointB = element.pointB;

        if (!currentPointA || !currentPointB) {
          logger.error('Unexpectedly had a undefined point on a line element');
          return element;
        }

        const center = getMiddlePoint(
          new Vector2(currentPointA.x, currentPointA.y),
          new Vector2(currentPointB.x, currentPointB.y),
        );

        const halfLineLength = sizes.crossLineLength / 2;
        const orthogonalPointOffset = halfLineLength / Math.sqrt(2);

        const points = element.tags.includes(FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A)
          ? {
              pointA: new Vector3(
                center.x - orthogonalPointOffset,
                center.y + orthogonalPointOffset,
                Z_INDEX_MAP[FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A],
              ),
              pointB: new Vector3(
                center.x + orthogonalPointOffset,
                center.y - orthogonalPointOffset,
                Z_INDEX_MAP[FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A],
              ),
            }
          : {
              pointA: new Vector3(
                center.x - orthogonalPointOffset,
                center.y - orthogonalPointOffset,
                Z_INDEX_MAP[FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B],
              ),
              pointB: new Vector3(
                center.x + orthogonalPointOffset,
                center.y + orthogonalPointOffset,
                Z_INDEX_MAP[FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B],
              ),
            };

        return {
          ...element,
          ...points,
          // NOTE: canvas should work without specifying this, since it is just the same element spread above, but it doesn't
          strokeWidth: REFERENCE_SCALE_ELEMENT_STROKE_WIDTH,
          autoStrokeWidth: true,
        };
      }

      return element;
    });

    // Scale origin elements (preserve current position and interactivity)
    const scale = calcScaleFactorBasedOnCanvasNativeZoom(
      OriginElementSizing.REFERENCE_ZOOM,
      canvas.getZoom(),
    );

    const currentOriginGroup = canvas.getElement(ORIGIN_GROUP_ID);
    const originInteractive = currentOriginGroup?.interactivity?.selectable ?? false;

    const originElements = buildOriginElements(scale, originInteractive).map((element) => {
      const currentElement = canvas.getElement(element.id);

      if (!currentElement) {
        return element;
      }

      if (element.id === ORIGIN_GROUP_ID) {
        return {
          ...element,
          position: currentElement.position,
          rotation: currentElement.rotation,
          interactivity: currentElement.interactivity,
        };
      }

      return {
        ...element,
        interactivity: currentElement.interactivity,
      };
    });

    canvas.updateElements([
      ...rackElements,
      ...arrowElements,
      ...connectionElements,
      ...jitRoutingPointElements,
      ...referenceScalingPointElements,
      ...originElements,
    ]);

    useLayerStore.getState().refreshVehicleGeneratedLayerVisibility();
  }

  public enableTargeting(targets: string[]) {
    const canvas = this.getCanvas();
    const { design } = this.floorplanDesignStore.getState();
    const { getClickableElementIds, getDisabledElementIds } = this.appStore.getState().enabling;
    const alreadyEnabledIds = getClickableElementIds();
    const alreadyDisabledIds = getDisabledElementIds();
    const elementsToUpdate: Element[] = [];
    const newTargets = targets.filter((id) => !alreadyEnabledIds.includes(id));

    // is target part of a template? then enable targeting for the template as well
    const templates = templatesToFlatCollection(design.templates);
    const templatesToUpdate = templates.filter((template) =>
      template.group.areaIds.some((areaId) => newTargets.includes(areaId)),
    );
    elementsToUpdate.push(
      ...canvas
        .getElements(templatesToUpdate.map((template) => template.group.id))
        .map((element) => this.setElementClickable(element, true)),
    );

    // enable targeting for new targets
    elementsToUpdate.push(
      ...canvas.getElements(newTargets).map((element) => {
        if (isIntersect(element.tags ?? [], FDDM_AREA_TAGS)) {
          return this.setElementAlpha(
            this.setElementClickable(element, true),
            Transparency.DEFAULT,
          );
        }

        return this.setElementStrokeAlpha(
          this.setElementClickable(element, true),
          Transparency.DEFAULT,
        );
      }),
    );

    // dim and disable non-targets that are not already disabled
    const allAreaAndFlowStopIds = [
      ...areasToFlatCollection(design.areas).map((area) => area.id),
      ...design.flows.flowStops.map((flowStop) => flowStop.id),
    ];
    const elementsToDisable = allAreaAndFlowStopIds.filter((id) => !targets.includes(id));
    const newNonTargets = elementsToDisable.filter((id) => !alreadyDisabledIds.includes(id));
    elementsToUpdate.push(
      ...canvas.getElements(newNonTargets).map((element) => {
        if (isIntersect(element.tags ?? [], FDDM_AREA_TAGS)) {
          return this.setElementAlpha(this.setElementClickable(element, false), Transparency.DIM);
        }
        return this.setElementStrokeAlpha(
          this.setElementClickable(element, false),
          Transparency.DIM,
        );
      }),
    );

    canvas.updateElements(elementsToUpdate);
    canvas.lockSelection();

    const appSvc = AppServiceFactory.getInstance().createAppService();
    appSvc.setDisabledElementIds(elementsToDisable);
    appSvc.setClickableElementIds(targets);
  }

  public enableTargetingForAllAreas() {
    const { design } = this.floorplanDesignStore.getState();
    const targets = areasToFlatCollection(design.areas).map((area) => area.id);
    this.enableTargeting(targets);
  }

  public disableTargeting() {
    const canvas = this.getCanvas();
    const elementsToUpdate: Element[] = [];

    const disabledElementIds = this.appStore.getState().enabling.getDisabledElementIds();
    const clickableElementIds = this.appStore.getState().enabling.getClickableElementIds();

    // reset clickable state of previously clickable elements
    elementsToUpdate.push(
      ...canvas
        .getElements(clickableElementIds)
        .map((element) => this.setElementClickable(element, false)),
    );

    // reset alpha of previously disabled elements
    elementsToUpdate.push(
      ...canvas.getElements(disabledElementIds).map((element) => {
        if (isIntersect(element.tags ?? [], FDDM_AREA_TAGS)) {
          return this.setElementAlpha(element, Transparency.DEFAULT);
        }
        return this.setElementStrokeAlpha(element, Transparency.DEFAULT);
      }),
    );
    const appSvc = AppServiceFactory.getInstance().createAppService();
    appSvc.setDisabledElementIds([]);
    appSvc.setClickableElementIds([]);

    canvas.updateElements(elementsToUpdate);
    canvas.unlockSelection();
    this.setInteractivityMode('EDITABLE');
    this.applyInteractivityMode();
  }

  public setCursor(cursor: CursorType) {
    const canvas = this.getCanvas();
    canvas.setCursor(cursor);
  }

  public resetCursor() {
    const canvas = this.getCanvas();
    canvas.setCursor(); // resets to 'default' behavior.
  }

  public setActiveTool(tool: ToolId): void {
    const canvas = this.getCanvas();
    const canvasTool = toolIdToCanvasTool(tool);

    if (!canvasTool) {
      return;
    }

    if (isSegmentToolId(tool)) {
      const stroke = Color.fromHex(modifyColor(toolIdToColor(tool), false, '22'));
      const strokeWidth = NumUtils.metersToMmRounded(
        tool === 'highway_angled'
          ? this.floorplanDesignStore.getState().getPreference('highwayArea.segment.width')
          : this.floorplanDesignStore.getState().getPreference('wall.thickness'),
      );

      canvas.activateTool(canvasTool, {
        stroke,
        strokeWidth,
        autoStrokeWidth: false,
      });
      return;
    }

    if (isDrawableToolId(tool)) {
      const color = modifyColor(toolIdToColor(tool), false, '22');
      canvas.activateTool(canvasTool, { fill: Color.fromHex(color) });
      return;
    }

    canvas.activateTool(canvasTool);
  }

  public setSelection(ids: string[]) {
    const canvas = this.getCanvas();
    canvas.setSelection(ids);
  }

  public alignElements(mode: AlignMode) {
    const canvas = this.getCanvas();

    switch (mode) {
      case AlignMode.LEFT:
        canvas.alignLeft();
        break;
      case AlignMode.CENTER:
        canvas.alignCenterHorizontal();
        break;
      case AlignMode.RIGHT:
        canvas.alignRight();
        break;
      case AlignMode.TOP:
        canvas.alignTop();
        break;
      case AlignMode.MIDDLE:
        canvas.alignCenterVertical();
        break;
      case AlignMode.BOTTOM:
        canvas.alignBottom();
        break;
      default:
        assertNever(mode);
    }
  }

  public zoomToInitialView() {
    const canvas = this.getCanvas();

    // Config
    const preferredViewportDimensionsInMeter = {
      width: 15,
      height: 15,
    };

    this.zoomToFit();

    const cameraPositionAfterInitialZoomToFit = canvas.getCameraState().position;

    // if camera position is still at initial position of 0,0 -> assume there are no floorplan elements to zoomFit
    const noCanvasElementsPresent =
      cameraPositionAfterInitialZoomToFit.x === 0 && cameraPositionAfterInitialZoomToFit.y === 0;

    const viewportElementTypeAndPosition = noCanvasElementsPresent
      ? // normal bottom left view
        ({
          type: 'Rect',
          position: new Vector3(0, 0, 0),
        } as const)
      : // view centered over existing floorplan elements
        ({
          type: 'RectCentered',
          position: new Vector3(
            cameraPositionAfterInitialZoomToFit.x,
            cameraPositionAfterInitialZoomToFit.y,
            0,
          ),
        } as const);

    // Add temp element to canvas, zoom to fit it, and remove it to clean up
    const baseViewportElementId = uuid();
    const viewportElement: RectElement | RectCenteredElement = {
      id: baseViewportElementId,
      name: 'base-viewport-element',
      type: viewportElementTypeAndPosition.type,
      position: viewportElementTypeAndPosition.position,
      size: new Vector2(
        preferredViewportDimensionsInMeter.width * METERS_TO_MILLIMETERS_FACTOR,
        preferredViewportDimensionsInMeter.height * METERS_TO_MILLIMETERS_FACTOR,
      ),
      interactivity: INTERACTIVITY_OFF,
    };
    canvas.updateElement(viewportElement);
    this.zoomToFitElements([baseViewportElementId]);
    canvas.removeElement(baseViewportElementId);

    // Update state
    const canvasNativeZoomLevel = canvas.getZoom();
    const nativeZoomToUserZoomFactor = 100 / canvasNativeZoomLevel;
    const { setZoomValues, setNativeZoomToUserZoomFactor } = this.canvasStore.getState();
    setZoomValues({
      zoomDisplayValue: 100,
      canvasNativeZoomLevel,
    });
    setNativeZoomToUserZoomFactor(nativeZoomToUserZoomFactor);
  }

  public zoomTo(zoomLevel: number) {
    const canvas = this.getCanvas();

    const { setZoomValues, nativeZoomToUserZoomFactor } = this.canvasStore.getState();

    const newCanvasNativeZoom = ZoomFactorUtils.calcDisplayToNativeZoomLevel(
      nativeZoomToUserZoomFactor,
      zoomLevel,
    );
    setZoomValues({
      canvasNativeZoomLevel: newCanvasNativeZoom,
      zoomDisplayValue: zoomLevel,
    });
    canvas.setZoom(newCanvasNativeZoom / 1000); // TODO: 1000 needs to be removed when canvas setZoom is fixed. It's a compensation for the * 1000 bug in the canvas
  }

  public zoomToFit() {
    const canvas = this.getCanvas();

    const excludedTagRegExp = buildTagMatchRegExp([FDDM_TAG.ORIGIN]);
    canvas.zoomToFit(excludedTagRegExp);
    canvas.zoomOut();
  }

  public zoomToFitElements(ids: string[]) {
    const canvas = this.getCanvas();
    canvas.zoomToFitElements(ids);
    canvas.zoomOut();
  }

  public zoomIn() {
    const canvas = this.getCanvas();
    canvas.zoomIn();
  }

  public zoomOut() {
    const canvas = this.getCanvas();
    canvas.zoomOut();
  }

  //
  // Element colors
  //

  setElementColor(id: string, color: string): void {
    const canvas = this.getCanvas();
    const element = canvas.getElement(id);
    if (!element || !Color.fromHex(color)) {
      logger.error(
        `Tried to set color of element with id ${id}, but it was not found in the canvas.`,
      );
      return;
    }
    const updatedElement = {
      ...element,
      fill: Color.fromHex(color),
    };
    canvas.updateElement(updatedElement);
  }

  canvasInstanceIsInitialized() {
    return !!this.canvasStore.getState().instance;
  }

  toggleElementsVisible(regexp: RegExp, value: boolean) {
    const canvas = this.getCanvas();
    const elements = canvas.getElementsByTag(regexp);
    canvas.setElementVisibility(
      elements.map((element) => element.id),
      value,
    );
  }

  //
  // Keyboard
  //

  disableKeyboard() {
    const canvas = this.getCanvas();
    canvas.disableKeyboardInput();
  }
  enableKeyboard() {
    const canvas = this.getCanvas();
    canvas.enableKeyboardInput();
  }

  public setInteractivityMode(
    mode: FloorplanInteractionMode,
    excludeElementIds: string[] = [],
  ): void {
    this.interactivityMode = mode;
    this.applyInteractivityMode(excludeElementIds);
  }

  public setReadonlySelectionMode(enabled: boolean, excludeElementIds: string[] = []): void {
    this.setInteractivityMode(enabled ? 'SELECT_ONLY' : 'EDITABLE', excludeElementIds);
  }

  //
  // 3D
  //

  prepare3DView(): void {
    const canvas = this.getCanvas();
    const {
      design: { areas, obstacles, logicalSpaces },
      setup,
    } = this.floorplanDesignStore.getState();

    this.toggleElementsVisible(TAG_PATTERN_2D_ELEMENTS, false);

    const mapper = new FloorplanElementMapper({
      areas: false,
      obstacles: false,
      logicalSpaces: false,
      jitRoutingConfigs: false,
      interAreaConnections: false,
      groups: false,
      templateGroups: false,
      flowStops: false,
      flows: false,
      stations: false,
      pois: false,
      gateMappings: false,
      tracks: false,
      referenceImage: false,
      model: true,
    });

    canvas.updateElements(
      mapper.map3D({
        areas,
        obstacles,
        loadTypes: setup.loadTypes,
        logicalSpaces,
      }),
    );
  }

  prepare2DView() {
    // show 2d elements
    this.toggleElementsVisible(TAG_PATTERN_2D_ELEMENTS, true);

    // remove 3d elements
    this.removeCanvasElements({
      method: 'byTag',
      tags: [FDDM_TAG.MODEL],
    });
  }

  //
  // private methods
  //

  private createLayerVisibilityResolver(zoom: number): {
    isTagVisible: (tag: Tag) => boolean;
    isLayerVisible: (layerName: LayerNames) => boolean;
    isGeneratedLayerVisible: (vehicleTypeId: string, layerName: GeneratedLayerNames) => boolean;
  } {
    const visibilityByLayer = this.getLayersVisibility(zoom);
    const { layers } = useLayerStore.getState();

    return {
      isTagVisible: (tag: Tag) => {
        const definitions = getLayerDefinitionsForTag(tag);

        if (definitions.length === 0) {
          return this.lodStore.getState().getGroupVisibility(tag) ?? true;
        }

        return definitions.every((definition) => visibilityByLayer[definition.name] ?? true);
      },
      isLayerVisible: (layerName: LayerNames) => visibilityByLayer[layerName] ?? true,
      isGeneratedLayerVisible: (vehicleTypeId: string, layerName: GeneratedLayerNames): boolean => {
        const definition = getLayerDefinition(layerName);
        if (!definition) return visibilityByLayer[layerName] ?? true;

        const vehicleLayerName = createVehicleGeneratedLayerName(vehicleTypeId, layerName);
        const vehicleLayer = layers[vehicleLayerName];

        if (!vehicleLayer) return visibilityByLayer[layerName] ?? true;

        const showFromZoom = this.getNativeZoomPreferenceValue(
          definition.lod?.showFromZoomPreference,
        );
        const hideFromZoom = this.getNativeHideFromZoomPreferenceValue(
          definition.lod?.hideFromZoomPreference,
        );

        return getEffectiveLayerVisibility({
          visibilityMode: vehicleLayer.visibilityMode,
          parentVisible: true,
          zoom,
          showFromZoom,
          hideFromZoom,
        });
      },
    };
  }

  private getLayersVisibility(zoom: number): Partial<Record<LayerNames, boolean>> {
    const { layers } = useLayerStore.getState();
    const visibilityByLayer: Partial<Record<LayerNames, boolean>> = {};

    FDDM_LAYER_DEFINITIONS.forEach((definition) => {
      const layer = layers[definition.name];
      const visibilityMode = layer?.visibilityMode ?? this.getDefaultVisibilityMode(definition);
      const parentVisible = true;
      const showFromZoom = this.getNativeZoomPreferenceValue(
        definition.lod?.showFromZoomPreference,
      );
      const hideFromZoom = this.getNativeHideFromZoomPreferenceValue(
        definition.lod?.hideFromZoomPreference,
      );

      visibilityByLayer[definition.name] = getEffectiveLayerVisibility({
        visibilityMode,
        parentVisible,
        zoom,
        showFromZoom,
        hideFromZoom,
      });
    });

    return visibilityByLayer;
  }

  private getDefaultVisibilityMode(definition: LayerDefinition): LayerVisibilityMode {
    if (definition.visibilityMode) return definition.visibilityMode;
    if (definition.lod) return 'auto';

    return 'force_show';
  }

  private getNativeZoomPreferenceValue(preference?: UserPreferenceName): number | undefined {
    if (!preference) return undefined;

    const value = getUserPreference(preference);
    if (typeof value !== 'number') return undefined;

    const { nativeZoomToUserZoomFactor } = this.canvasStore.getState();
    if (nativeZoomToUserZoomFactor <= 0) return value;

    return value / nativeZoomToUserZoomFactor;
  }

  private getNativeHideFromZoomPreferenceValue(
    preference?: UserPreferenceName,
  ): number | undefined {
    if (preference !== UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD) {
      return this.getNativeZoomPreferenceValue(preference);
    }

    if (this.floorplanDesignStore.getState().tracks?.length) {
      return this.getNativeZoomPreferenceValue(preference);
    }

    return undefined;
  }

  /**
   * @description
   * Private getter for the canvas instance which exposes its api.
   */
  private getCanvas = (): Canvas => {
    const canvas = this.canvasStore.getState().instance;

    if (!canvas) {
      throw new Error('Canvas instance not available.');
    }

    return canvas;
  };

  private applyInteractivityMode(excludeElementIds: string[] = []): void {
    const canvas = this.getCanvas();
    const excludedElementIds = new Set(excludeElementIds);

    const elements = canvas
      .getElementsByTag(
        buildTagMatchRegExp([
          FDDM_TAG.SINGULAR_STATION_AREA,
          FDDM_TAG.STATIONARY_AREA,
          FDDM_TAG.RACKING_AREA,
          FDDM_TAG.HIGHWAY_AREA,
          FDDM_TAG.MANUAL_AREA,
          FDDM_TAG.OBSTACLE,
          FDDM_TAG.LOGICAL_SPACE,
          FDDM_TAG.TEMPLATE,
          FDDM_TAG.GROUP,
        ]),
      )
      .filter((element) => !excludedElementIds.has(element.id));
    const ids = elements.map((element) => element.id);

    const bubble_elements = canvas
      .getElementsByTag(buildTagMatchRegExp([FDDM_TAG.BUBBLE]))
      .filter((element) => !excludedElementIds.has(element.id));
    const bubble_ids = bubble_elements.map((element) => element.id);

    const segment_elements = canvas
      .getElementsByTag(buildTagMatchRegExp([FDDM_TAG.ANGLED_HIGHWAY_AREA, FDDM_TAG.WALL]))
      .filter((element) => !excludedElementIds.has(element.id));
    const segment_ids = segment_elements.map((element) => element.id);

    if (ids.length === 0 && bubble_ids.length === 0 && segment_ids.length === 0) {
      return;
    }

    canvas.updateInteractivity(ids, INTERACTIVITY_MODE_MAP[this.interactivityMode]);
    canvas.updateInteractivity(
      bubble_ids,
      CONNECTION_BUBBLE_INTERACTIVITY_MODE_MAP[this.interactivityMode],
    );
    canvas.updateInteractivity(
      segment_ids,
      SEGMENT_ELEMENT_INTERACTIVITY_MODE_MAP[this.interactivityMode],
    );
  }

  private withPreservedInteractivity(element: Element, canvas: Canvas): Element {
    const currentElement = canvas.getElement(element.id);

    if (currentElement) {
      return {
        ...element,
        interactivity: currentElement.interactivity,
      };
    }

    if (!isIntersect(element.tags ?? [], FDDM_INTERACTIVE_TAGS)) {
      return element;
    }

    return {
      ...element,
      interactivity: this.getInteractivityForCurrentMode(),
    };
  }

  private getInteractivityForCurrentMode(): Element['interactivity'] {
    return INTERACTIVITY_MODE_MAP[this.interactivityMode];
  }

  private setElementClickable = (element: Element, value: boolean): Element => ({
    ...element,
    interactivity: {
      ...element.interactivity,
      clickable: value,
    },
  });

  private setElementAlpha = (element: Element, alpha: number) => {
    if (element.type === 'RectCentered' && element.fill) {
      return {
        ...element,
        fill: Color.fromRGBA(element.fill.r, element.fill.g, element.fill.b, alpha),
      };
    }

    return element;
  };

  private setElementStrokeAlpha = (element: Element, alpha: number) => {
    if (element.type === 'RectCentered' && element.stroke) {
      return {
        ...element,
        stroke: Color.fromRGBA(element.stroke.r, element.stroke.g, element.stroke.b, alpha),
      };
    }

    return element;
  };

  private scaleIconElements = (
    elements: ReturnType<Canvas['getElementsByTag']>,
    scale: number,
  ): RectCenteredElement[] =>
    elements.flatMap((element): RectCenteredElement[] => {
      if (element.type !== 'RectCentered') return [];

      return [
        {
          ...element,
          type: 'RectCentered',
          rotation: new Vector3(
            0,
            0,
            element.rotation?.z ? roundToDecimals(element.rotation.z, 3) : 0, // round to fix rotation drifting
          ),
          size: new Vector2(scale, scale),
        },
      ];
    });
}
